import { UISettings } from "@/types/ui";
import defensiveCopy from "@/util/defensive-copy";
import { uiStore } from "@/store/store-accessor";
import fpJsonParse from "@/util/fp-json-parse";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { readFromLocalStorage } from "@/util/localstorage";

export const UI_SETTINGS_LOCAL_STORAGE_KEY = "breakdance-ui-settings_theme";

export function getBrowserPreferredTheme(): UISettings["theme"] {
  // The browser's color scheme  is stored as a media query
  if (typeof window.matchMedia === "function") {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const hasMediaQueryPreference = typeof mql.matches === "boolean";
    if (hasMediaQueryPreference) {
      return mql.matches ? "dark" : "light";
    }
  }

  // Default to "light" if the browser doesn't support color scheme
  return "light";
}

export const uiSettingsDefaults: UISettings = {
  autoScrollEnabled: true,
  showExperimentalElements: false,
  scaleToFit: "SCALED_TO_FIT",
  theme: getBrowserPreferredTheme(),
  showElementIdInStructurePanel: false,
  disableNoticeForEmptySSRElements: false,
  popoutPosition: "STACKED",
  disableCodeMirrorLineWrapping: false,
  isTinyMcePreferredForRichText: true,
  includeCustomBreakpointsInBreakpointsDropdown: false,
  defaultUnitType: "px",
  disableCustomUnitDropdown: false,
  showPreviousBreakpointValueAsPlaceholder: false
};

export function getUiSettingFromLocalStorageOrDefaults(): UISettings {
  const uiSettingsFromLocalStorageAsJsonString = readFromLocalStorage(
    UI_SETTINGS_LOCAL_STORAGE_KEY
  );

  if (!uiSettingsFromLocalStorageAsJsonString) return uiSettingsDefaults;

  return pipe(
    fpJsonParse(uiSettingsFromLocalStorageAsJsonString),
    fold(
      () => uiSettingsDefaults,
      // TODO use io-ts decoder instead of "as"
      uiSettingsParsedFromJson => uiSettingsParsedFromJson as UISettings
    )
  );
}

export function setScaleToFitAndGetSettings(newScaleToFit: boolean) {
  const settings = defensiveCopy(uiStore.settings);
  settings.scaleToFit = newScaleToFit ? "SCALED_TO_FIT" : "ACTUAL_SIZE";
  // Scroll to the left to prevent the view from getting stuck on the right if they scrolled while zoomed
  document.getElementById("iframe-wrapper")?.scroll({
    left: 0
  });

  uiStore.setSettings(settings);

  return settings;
}
